import { useState } from 'react';

import type { DealListItem, VisibleToEntry } from '../../types';
import { badgeStyleFor, emailToShortName, formatEtShort } from './DealsPage.shared';
import styles from './DealsPage.module.css';

/** Deal list row + group section + visible-to chip row, extracted verbatim
 *  from DealsPage.tsx (D4 componentization, zero behavior change). */

interface DealListRowProps {
  item: DealListItem;
  selected: boolean;
  viewerEmail: string;
  onClick: () => void;
  // Slice J §J2 — bulk-select checkbox state. Optional because list rows
  // were render-only pre-J2; the BulkActionToolbar wires these props.
  bulkSelected?: boolean;
  onToggleSelect?: (deal_key: string) => void;
}

function DealListRow({
  item,
  selected,
  viewerEmail: _viewerEmail,
  onClick,
  bulkSelected = false,
  onToggleSelect,
}: DealListRowProps) {
  const lastEvent = item.last_event_at ? formatEtShort(item.last_event_at) : '—';
  const badgeClass =
    item.badge === 'NEW_DEAL' ? styles.badgeNewDeal : styles.badgeFieldUpdate;
  const badgeText = item.badge === 'NEW_DEAL' ? 'New Deal' : 'Update';
  const pendingTooltip =
    `${item.pending_count} unresolved field proposal${item.pending_count === 1 ? '' : 's'}` +
    ' — click into the deal to accept / edit / dismiss.';

  return (
    <div
      role="button"
      tabIndex={0}
      className={`${styles.listItem} ${selected ? styles.listItemActive : ''}`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className={styles.listItemLine1}>
        {onToggleSelect && (
          <input
            type="checkbox"
            className={styles.bulkCheckbox}
            checked={bulkSelected}
            onClick={(e) => e.stopPropagation()}
            onChange={() => onToggleSelect(item.deal_key)}
            aria-label="Select deal for bulk actions"
          />
        )}
        <span className={`${styles.badge} ${badgeClass}`} style={badgeStyleFor(item.badge)}>
          {badgeText}
        </span>
        <span className={styles.displayName}>{item.display_name}</span>
        {item.pending_count > 0 && (
          <span className={styles.pendingChip} title={pendingTooltip}>
            ●{item.pending_count}
          </span>
        )}
        {item.unpushed_field_count > item.pending_count && (
          <span
            className={styles.needsPushChip}
            title="Reviewed but not yet pushed to Affinity — open the card and Push Updates"
          >
            ⤴ needs push
          </span>
        )}
        {item.needs_aggregation && (
          <span
            className={styles.aggregatingChip}
            title="Re-aggregating after a recent change — this card will refresh automatically"
          >
            ⟳ aggregating…
          </span>
        )}
      </div>
      <div className={styles.listItemLine2}>
        <span>{item.deal_lead ?? '—'}</span>
        <span>·</span>
        <span>{lastEvent}</span>
        {item.stage_abbrev && (
          <>
            <span>·</span>
            <span>{item.stage_abbrev}</span>
          </>
        )}
        {item.is_add_on && (
          <>
            <span>·</span>
            <span>+Add-On</span>
          </>
        )}
        {/* Wave C #13 — stale indicator. Only show when ≥ 14 days. */}
        {item.days_since_last_event !== null && item.days_since_last_event >= 14 && (
          <>
            <span>·</span>
            <span
              className={styles.staleBadge}
              title={`No events in ${item.days_since_last_event} days`}
            >
              ⏳ stale {item.days_since_last_event}d
            </span>
          </>
        )}
        {/* Wave C #14 — frequent-unbinds badge. Surfaces resolver
            over-binding patterns. */}
        {item.refute_count_7d >= 3 && (
          <>
            <span>·</span>
            <span
              className={styles.unbindsBadge}
              title={`${item.refute_count_7d} operator refute(s) on this deal in the last 7 days — resolver may be over-binding evidence to it`}
            >
              ⚠ frequent unbinds
            </span>
          </>
        )}
      </div>
    </div>
  );
}

interface GroupSectionProps {
  group: { key: string; label: string; items: DealListItem[] };
  showHeader: boolean;
  selectedKey: string | null;
  viewerEmail: string;
  onSelect: (key: string) => void;
  selectedSet: Set<string>;
  onToggleSelect: (deal_key: string) => void;
}

export function GroupSection({
  group,
  showHeader,
  selectedKey,
  viewerEmail,
  onSelect,
  selectedSet,
  onToggleSelect,
}: GroupSectionProps) {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <div>
      {showHeader && (
        <div
          className={styles.groupHeader}
          onClick={() => setCollapsed((c) => !c)}
        >
          <span>{collapsed ? '▸' : '▾'}</span>
          <span>{group.label}</span>
          <span className={styles.groupCount}>{group.items.length}</span>
        </div>
      )}
      {!collapsed && group.items.map((item) => (
        <DealListRow
          key={`${group.key}:${item.deal_key}`}
          item={item}
          selected={item.deal_key === selectedKey}
          viewerEmail={viewerEmail}
          onClick={() => onSelect(item.deal_key)}
          bulkSelected={selectedSet.has(item.deal_key)}
          onToggleSelect={onToggleSelect}
        />
      ))}
    </div>
  );
}

interface VisibleToRowProps {
  entries: VisibleToEntry[];
  dealKey: string;
  dealKind: 'candidate' | 'affinity_opp';
}

// Slice J §1b/G2 — full visibility chip set with provenance tooltip.
// Tooltip: "Caleb J. — via inbox + sender · 3 events · high confidence".
// Click → Pipeline Insights Investigate for this deal.
export function VisibleToRow({ entries, dealKey, dealKind }: VisibleToRowProps) {
  return (
    <div className={styles.visibleToRow}>
      <span className={styles.visibleToLabel}>Visible to:</span>
      {entries.map((e) => {
        // Per-user trace filtering is not a Pipeline Insights query
        // param today; open the deal in Investigate mode. Per-user
        // drilldown is queued for a later PI iteration.
        const traceUrl = `/?tab=pipeline-insights&deal_kind=${dealKind}&deal_key=${encodeURIComponent(dealKey)}`;
        const sourceLabel: Record<string, string> = {
          inbox: 'inbox',
          sender: 'sender',
          recipient: 'recipient',
          official_team: 'deal team',
          proposed_team: 'proposed team',
        };
        const sources = e.sources.length > 0
          ? `via ${e.sources.map((s) => sourceLabel[s] ?? s).join(' + ')}`
          : '';
        // Team-derived entries (deal-team audience flag) have no evidence
        // basis: event_count is 0 and top_confidence is null. Drop those
        // segments so the tooltip doesn't read "0 events · null confidence".
        const title = [
          e.email,
          sources,
          e.event_count > 0 ? `${e.event_count} event${e.event_count === 1 ? '' : 's'}` : '',
          e.top_confidence ? `${e.top_confidence} confidence` : '',
        ].filter(Boolean).join(' · ');
        return (
          <a
            key={e.email}
            href={traceUrl}
            className={styles.visibleToChip}
            title={title}
          >
            {emailToShortName(e.email)}
          </a>
        );
      })}
    </div>
  );
}
